import React, { useEffect, useRef, useState } from 'react';
import useIntersectionObserver from '@react-hook/intersection-observer';
import { styled } from '@mui/material';
import { useTrimOldMessages } from './hooks';
import { useSelector } from 'react-redux';
import { getRenderableMessages } from '../../../../reduxStore/selectors/messages.selector';

/**
 * Sentinel element rendered at the bottom of the legacy reverse list.
 *
 * Because the container uses `flex-direction: column-reverse`, the first DOM
 * child appears visually at the bottom of the scroll area (where the newest
 * messages are). When this sentinel enters the viewport the user is at the
 * bottom of the conversation, so old messages are trimmed from redux to keep
 * the in-memory buffer bounded.
 *
 * The trim is also re-triggered whenever the renderable message count changes
 * while the sentinel is still visible (e.g. new messages arrive while the user
 * stays at the bottom), so the buffer never grows unbounded in long sessions.
 *
 * The `prevIsIntersecting` guard prevents a spurious trim on mount before the
 * user has actually scrolled to the bottom.
 */
export const BottomObserver = styled(
	({ className }: { className?: string }) => {
		const [ref, setRef] = useState<HTMLElement | null>(null);
		const { isIntersecting } = useIntersectionObserver(ref, {
			rootMargin: '0px 0px 20px 0px',
			threshold: 1,
		});
		const prevIsIntersecting = useRef<boolean | null>(null);
		const { trim } = useTrimOldMessages();
		const renderableCount = useSelector(getRenderableMessages).length;

		// Trigger trim on transition into the viewport.
		useEffect(() => {
			if (isIntersecting && !prevIsIntersecting.current)
				window.setTimeout(trim, 100);
			prevIsIntersecting.current = isIntersecting;
		}, [isIntersecting, trim]);

		// Re-trigger trim while remaining at the bottom as new messages arrive,
		// preventing the buffer from growing beyond the configured limit.
		useEffect(() => {
			if (isIntersecting) trim();
		}, [renderableCount, isIntersecting, trim]);

		return <div ref={setRef} className={className} />;
	},
	{ target: 'BottomObserver-root', label: 'BottomObserver' },
)({
	height: '1px',
	width: '100%',
});
